// axiosConfig.tsx
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { transformedResponse } from 'util/transformedResponse';
import AuthService from 'services/authService';

// Create an axios instance
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  transformResponse: [
    ...axios.defaults.transformResponse as any,
    (data: any) => transformedResponse(data as AxiosResponse),
  ],
});

// Request interceptor: if the request is for /api/user and no access token is set, try refreshing first.
api.interceptors.request.use(
  async (config) => {
    let token = AuthService.getAccessToken();
    // If there's no token and we're about to call the user endpoint, attempt a refresh.
    if (!token && config.url?.includes('/api/')) {
      try {
        await AuthService.refresh();
        token = AuthService.getAccessToken();
      } catch (err) {
        // If refresh fails, token remains null, and the request will likely 401.
      }
    }
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, try to refresh and retry.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      originalRequest._retry = true;
      try {
        await AuthService.refresh();
        const newToken = AuthService.getAccessToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        AuthService.clearAccessToken();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
